// This file is automatically generated by webgen from the struct descriptions in /generated-json/structs.
// Files in /generated-json are created from the structs configured in the 'main' function of /cmd/struct2json/main.go.
//
// DO NOT EDIT THIS FILE except where designated below.

/* eslint-disable no-relative-import-paths/no-relative-import-paths */

// webgen:import { "names": ["DateTime"], "path": "luxon" }
// webgen:import { "names": ["getDefaultUnitForCategory"], "path": "../../../utils/forecastDistribution" }
// webgen:import { "typeNames": ["Units"], "path": "../../units" }

import { DateTime, type Duration } from 'luxon';
import type { PartialDeep } from 'type-fest';

import * as DateHelpers from '../../../utils/dateHelpers';
import { getDefaultUnitForCategory } from '../../../utils/forecastDistribution';
import type { ExcludeMethodsDeep } from '../../../utils/types';
import { NIL_UUID } from '../../../utils/uuid';
import type { Units } from '../../units';
import Enums from '../Enums';

export class Forecast {
  readonly id: string;
  readonly kind: Enums.ForecastKind;
  readonly projectId: string | null;
  readonly plantId: string | null;
  readonly divisionId: string | null;
  readonly value: string;
  readonly unit: Enums.Unit;
  readonly productCategory: Enums.ProductCategory;
  readonly intervalLength: Duration;
  readonly intervalStart: DateTime;

  constructor(data: { [key: string]: any } = {}) {
    this.id = data.id === undefined ? NIL_UUID : data.id;
    this.kind = data.kind === undefined ? Enums.ForecastKind.Budget : data.kind;
    this.projectId = data.projectId === undefined ? null : data.projectId;
    this.plantId = data.plantId === undefined ? null : data.plantId;
    this.divisionId = data.divisionId === undefined ? null : data.divisionId;
    this.value = data.value === undefined ? '0' : data.value;
    this.unit = data.unit === undefined ? Enums.Unit.Ac : data.unit;
    this.productCategory =
      data.productCategory === undefined ? Enums.ProductCategory.ReadyMix : data.productCategory;
    this.intervalLength = DateHelpers.ParseServerDuration(data.intervalLength);
    this.intervalStart = DateHelpers.ParseServerDate(data.intervalStart);
  }

  static zero(): Forecast {
    const zeroValues: ExcludeMethodsDeep<Forecast> = {
      id: NIL_UUID,
      kind: Enums.ForecastKind.Budget,
      projectId: null,
      plantId: null,
      divisionId: null,
      value: '0',
      unit: Enums.Unit.Ac,
      productCategory: Enums.ProductCategory.ReadyMix,
      intervalLength: DateHelpers.ParseServerDuration(DateHelpers.ZERO_DURATION_STRING),
      intervalStart: DateHelpers.ParseServerDate(DateHelpers.ZERO_DATE_STRING),
    };
    return new Forecast(zeroValues);
  }

  // ************* DO NOT EDIT ABOVE THIS LINE *************

  /**
   * Given a start and end number of years + current forecasts, return a fully filled
   * forecast list with existing values filled in.
   */
  public static fillForecasts = ({
    currentForecasts,
    kind,
    productCategory,
    tenantUnits,
    from = DateHelpers.DEFAULT_FORECAST_YEAR_FROM,
    to = DateHelpers.DEFAULT_FORECAST_YEAR_TO,
  }: {
    /** The current forecasts which will override blank "fill" values. */
    currentForecasts: Forecast[];
    kind: Enums.ForecastKind;
    /** The product category for these forecasts (Mix, Aggregate, Asphalt, etc.). Required for Project forecasts, optional for Budget/Capacity */
    productCategory?: Enums.ProductCategory;
    /**
     * The tenant's default units. The generated forecasts are persisted with
     * the unit set here, and the forecast inputs are labelled with the tenant
     * default, so these MUST agree -- otherwise a metric tenant stores yd³
     * values under a m³ label and every downstream aggregation is off by the
     * conversion factor.
     */
    tenantUnits: Units;
    /**
     * The number of years previous to the current year.
     * @default DateHelpers.DEFAULT_FORECAST_YEAR_FROM
     */
    from?: number;
    /**
     * The number of years in the future to fill up-to.
     * @default DateHelpers.DEFAULT_FORECAST_YEAR_TO
     */
    to?: number;
  }): Forecast[] => {
    let fromDate = DateTime.now().plus({ years: from }).startOf('year');
    const toDate = DateTime.now()
      .plus({ years: to + 1 })
      .startOf('year');

    // Budget/capacity forecasts have no product category and are always dry volume.
    const defaultUnit = getDefaultUnitForCategory(
      productCategory ?? Enums.ProductCategory.ReadyMix,
      tenantUnits,
    );

    const forecasts: Forecast[] = [];
    for (
      ;
      fromDate.startOf('month') < toDate.startOf('month');
      fromDate = fromDate.plus({ month: 1 })
    ) {
      const existingForecast = currentForecasts.find(
        // eslint-disable-next-line no-loop-func
        (f) =>
          f.kind === kind &&
          (productCategory === undefined || f.productCategory === productCategory) &&
          f.intervalStart.toISO() === fromDate.toISO(),
      );
      if (existingForecast !== undefined) {
        forecasts.push(existingForecast);
      } else {
        const forecastData: Partial<ExcludeMethodsDeep<Forecast>> = {
          kind,
          intervalLength: DateHelpers.ParseServerDuration('P1M'),
          intervalStart: DateHelpers.ParseServerDate(fromDate.startOf('month').toISO()),
          unit: defaultUnit,
          productCategory,
          value: '',
        };
        forecasts.push(new Forecast(forecastData));
      }
    }
    return forecasts;
  };

  // ************* DO NOT EDIT BELOW THIS LINE *************
}

export type ForecastWireFormat = {
  id: string;
  kind: string;
  projectId: string | null;
  plantId: string | null;
  divisionId: string | null;
  value: string;
  unit: string;
  productCategory: string;
  intervalLength: string;
  intervalStart: string;
};

type NewForecastProps = PartialDeep<Forecast, { recurseIntoArrays: true }>;
export const NewForecast: (props: NewForecastProps) => Forecast = (props) => new Forecast(props);

type NewForecastFromWireFormatProps = PartialDeep<ForecastWireFormat, { recurseIntoArrays: true }>;
export const NewForecastFromWireFormat: (props: NewForecastFromWireFormatProps) => Forecast = (
  props,
) => new Forecast(props);
